import React, { useState, useEffect } from 'react';
import { generateVisualConcept, generateSketch, modifySketch } from './services/geminiService';
import { saveSketchToHistory, deductCredit } from './services/supabaseService';
import { LoadingState, GenerationResult } from './types';
import Header from './components/Header';
import InputArea from './components/InputArea';
import ResultCard from './components/ResultCard';
import LoadingSpinner from './components/LoadingSpinner';
import HistoryGallery from './components/HistoryGallery';
import AuthForm from './components/AuthForm';
import PricingModal from './components/PricingModal';
import Footer from './components/Footer';
import PrivacyPolicy from './components/PrivacyPolicy';
import TermsOfService from './components/TermsOfService';
import { useAuth } from './contexts/AuthContext';
import { AlertCircle, CheckCircle2 } from 'lucide-react';

// Define all possible views
type ViewState = 'create' | 'history' | 'privacy' | 'terms';

const App: React.FC = () => {
  const [currentView, setCurrentView] = useState<ViewState>('create');
  const { user, credits, refreshCredits } = useAuth();
  
  // Pricing Modal State
  const [isPricingOpen, setIsPricingOpen] = useState(false);
  const [paymentSuccess, setPaymentSuccess] = useState(false);

  // Generator State
  const [loadingState, setLoadingState] = useState<LoadingState>(LoadingState.IDLE);
  const [result, setResult] = useState<GenerationResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  // Check for payment success parameter in URL
  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    if (query.get('payment') === 'success') {
      setPaymentSuccess(true);
      refreshCredits();
      // Clean URL
      window.history.replaceState({}, document.title, "/");
      // Hide message after 5 seconds
      setTimeout(() => setPaymentSuccess(false), 5000);
    }
  }, [refreshCredits]);

  const handleGenerate = async (thought: string, watermark: string, image?: { data: string; mimeType: string }) => {
    // CREDIT CHECK
    if (user && credits !== null && credits <= 0) {
        setIsPricingOpen(true);
        return;
    }

    setLoadingState(LoadingState.CONCEPTUALIZING);
    setError(null);
    setResult(null);

    try {
      // Prepare multimodal input if image exists
      const imagePart = image ? {
        inlineData: {
            data: image.data,
            mimeType: image.mimeType
        }
      } : undefined;

      // Step 1: Get the concept and prompt from Gemini
      const conceptData = await generateVisualConcept(thought, imagePart);
      
      setLoadingState(LoadingState.GENERATING_IMAGE);

      // Step 2: Generate the image using Imagen
      const imageUrl = await generateSketch(conceptData.image_generation_prompt);

      setResult({
        concept: conceptData.visual_concept,
        prompt: conceptData.image_generation_prompt,
        imageUrl: imageUrl,
        watermarkText: watermark
      });
      
      // Step 3: Save & Deduct Credits (if user is logged in)
      if (user) {
        // Deduct credit
        const success = await deductCredit(user.id, credits || 0);
        if (success) {
            refreshCredits(); // Update UI header
        }

        // Save to vault
        saveSketchToHistory(
            user.id, 
            imageUrl, 
            conceptData.visual_concept, 
            conceptData.image_generation_prompt
        ).then(saved => {
            if (saved) console.log("Sketch saved to vault");
        });
      }
      
      setLoadingState(LoadingState.COMPLETED);
    } catch (err) {
      console.error(err);
      setError("Could not bring this thought to life. Please try again.");
      setLoadingState(LoadingState.ERROR);
    }
  };

  const handleModify = async (instruction: string) => {
    // CREDIT CHECK FOR MODIFY
    if (user && credits !== null && credits <= 0) {
        setIsPricingOpen(true);
        return;
    }

    if (!result) return;
    
    setLoadingState(LoadingState.GENERATING_IMAGE);
    setError(null);
    
    try {
        const newImageUrl = await modifySketch(result.imageUrl, instruction);
        
        const newResult = {
            ...result,
            imageUrl: newImageUrl
        };
        
        setResult(newResult);

        // Save the modified version & Deduct Credit
        if (user) {
             const success = await deductCredit(user.id, credits || 0);
             if (success) refreshCredits();

             saveSketchToHistory(
                user.id, 
                newImageUrl, 
                `${result.concept} (Modified)`, 
                result.prompt
            );
        }

        setLoadingState(LoadingState.COMPLETED);
    } catch (err) {
        console.error(err);
        setError("Could not modify the sketch. Please try again.");
        setLoadingState(LoadingState.ERROR);
    }
  };

  const handleReset = () => {
    setResult(null);
    setLoadingState(LoadingState.IDLE);
    setError(null);
  };

  // Render Main Content based on View State
  const renderContent = () => {
    if (currentView === 'privacy') {
        return <PrivacyPolicy onBack={() => setCurrentView('create')} />;
    }
    if (currentView === 'terms') {
        return <TermsOfService onBack={() => setCurrentView('create')} />;
    }
    if (currentView === 'history') {
        return user ? <HistoryGallery /> : <AuthForm />;
    }

    // Default: Create View
    return (
        <>
            {/* Landing Text - Fade out when result exists to save space */}
            {!result && loadingState === LoadingState.IDLE && (
                <div className="text-center mt-4 mb-8 px-4 space-y-2 animate-in fade-in slide-in-from-bottom-4 duration-700">
                    <h2 className="text-4xl md:text-5xl font-bold text-gray-900 font-hand tracking-tight rotate-1 leading-tight">
                    <span className="highlight">Turn ideas into sketches.</span>
                    </h2>
                    <p className="text-lg text-gray-500 max-w-lg mx-auto font-hand pt-4">
                        Create unique visual metaphors that stop the scroll.<br/>Scribble an idea below or upload a reference, and watch it come to life.
                    </p>
                </div>
            )}

            {/* Input Area */}
            <div className="w-full z-10">
                <InputArea onGenerate={handleGenerate} isProcessing={loadingState !== LoadingState.IDLE && loadingState !== LoadingState.COMPLETED} />
            </div>

            {/* Loading State */}
            {(loadingState === LoadingState.CONCEPTUALIZING || loadingState === LoadingState.GENERATING_IMAGE) && (
            <LoadingSpinner step={loadingState} />
            )}

            {/* Error State */}
            {loadingState === LoadingState.ERROR && (
            <div className="mt-8 p-6 bg-white border-2 border-black shadow-[4px_4px_0px_0px_rgba(239,68,68,0.2)] text-red-600 rounded-xl flex flex-col items-center gap-3 max-w-md mx-4 animate-in fade-in transform -rotate-1">
                <AlertCircle size={32} />
                <p className="font-hand text-lg text-center">{error}</p>
                <button onClick={handleReset} className="underline font-bold mt-2 hover:text-red-800">Try again</button>
            </div>
            )}

            {/* Success Result */}
            {loadingState === LoadingState.COMPLETED && result && (
                <div className="mt-12 w-full">
                    <ResultCard result={result} onReset={handleReset} onModify={handleModify} />
                </div>
            )}
        </>
    );
  };

  return (
    <div className="min-h-screen flex flex-col relative overflow-x-hidden">
      
      <Header 
        currentView={currentView === 'create' || currentView === 'history' ? currentView : 'create'} 
        onNavigate={(view) => setCurrentView(view)} 
        onOpenPricing={() => setIsPricingOpen(true)} 
      />
      
      <PricingModal isOpen={isPricingOpen} onClose={() => setIsPricingOpen(false)} />

      {/* Payment Success Toast */}
      {paymentSuccess && (
        <div className="fixed top-20 left-1/2 transform -translate-x-1/2 z-50 animate-in slide-in-from-top-4 fade-in">
            <div className="bg-green-100 border-2 border-green-600 text-green-800 px-6 py-3 rounded-lg shadow-lg flex items-center gap-3 font-hand font-bold">
                <CheckCircle2 className="text-green-600" />
                <span>Ink refilled! You're ready to sketch.</span>
            </div>
        </div>
      )}

      <main className="flex-grow flex flex-col items-center max-w-4xl mx-auto w-full p-4 pb-16 relative z-1">
        {renderContent()}
      </main>

      <Footer onNavigate={setCurrentView} />
    </div>
  );
};

export default App;